import { useState, Fragment } from 'react';
import {
    FlexGroup,
    Form,
    Input,
    InputGroup,
    Label,
    RadioGroup,
    FormRadioState,
    CheckBoxes,
} from './FormComponents';
import { ButtonPrimary } from './layout/StyledComponents';

const removeEmptyValues = (object: { [index: string]: FormDataEntryValue }) => {
    for (const key in object) {
        if (object.hasOwnProperty(key)) {
            const value = object[key];
            if (value === null || value === undefined || value === '' || value === '+') {
                delete object[key];
            }
        }
    }
};

const childTreatments = [
    'Heart disease',
    'Congenital birth defects',
    'Mental delays',
    'Liver/GI disease',
    'Recurrent headaches',
    'Physical delays',
    'Kidney disease',
    'Asthma',
    'Thyroid disease',
    'Speech/Hearing',
    'Diabetes',
    'Drug/Alcohol abuse',
    'Cerebral palsy',
    'Hepatitis',
    'Tuberculosis',
    'Cancer/Tumors',
    'Cleft lip/palate',
    'Bladder diff',
    'Bleeding/Transfusions',
    'Personality/Social',
    'Chicken pox',
    'Anemia',
    'Strep throat',
    'Measles/Mumps',
    'Rheumatic fever',
    'Blood dyscrasias',
    'Other problems',
    'Seizures/Epilepsy',
    'AIDS/HIV',
];

const childProblems = [
    'Cavities',
    'Toothache',
    'Teeth Sensitive',
    'Trauma',
    'Gum Infections',
    'Color of Teeth',
    'Orthodontics',
    'Jaw Sounds',
    'Other',
];

export const PatientForm = () => {
    const [submit, setSubmit] = useState<'init' | 'submitting' | 'submitted' | 'error'>('init');
    const [additionalInsurance, setAdditionalInsurance] = useState(false);

    const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmit('submitting');

        const formData = new FormData(e.currentTarget);
        const data = Object.fromEntries(formData);

        data['childTreatments'] = formData.getAll('childTreatments').join(', ');
        data['childProblems'] = formData.getAll('childProblems').join(', ');

        removeEmptyValues(data);

        await fetch(`https://api.${window.location.host}/contact`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then(() => {
                setSubmit('submitted');
            })
            .catch(() => {
                setSubmit('error');
            });
        e.target.reset();
    };

    return (
        <Form onSubmit={handleSubmit}>
            <span className="form-divider" />
            <h3>Patient Personal Information</h3>
            <FlexGroup twoOne>
                <Label htmlFor="patientName">
                    <span>Patient Name</span>
                    <Input
                        type="text"
                        id="patientName"
                        name="patientName"
                        placeholder="Patient Name"
                    />
                </Label>
                <Label htmlFor="nickName">
                    <span>Nick Name</span>
                    <Input
                        type="text"
                        id="nickName"
                        name="patientNickName"
                        placeholder="Nick Name"
                    />
                </Label>
            </FlexGroup>
            <FlexGroup radioText>
                <div>
                    <p>Sex</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input type="radio" id="sex-1" name="patientSex" value="Male" />
                            <Label htmlFor="sex-1">Male</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input type="radio" id="sex-2" name="patientSex" value="Female" />
                            <Label htmlFor="sex-2">Female</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
                <Label htmlFor="birthday">
                    <span>Birthday</span>
                    <Input
                        type="date"
                        id="birthday"
                        name="patientBirthday"
                        placeholder="Birthday"
                    />
                </Label>
                <Label htmlFor="age">
                    <span>Age</span>
                    <Input type="number" id="age" name="patientAge" placeholder="10" />
                </Label>
                <Label htmlFor="grade">
                    <span>Grade</span>
                    <Input type="text" id="grade" name="patientGrade" placeholder="Grade" />
                </Label>
            </FlexGroup>

            <FlexGroup oneTwo>
                <Label htmlFor="school">
                    <span>School</span>
                    <Input type="text" id="school" name="patientSchool" placeholder="School" />
                </Label>

                <Label htmlFor="siblingsInfo">
                    <span>Names & ages of siblings</span>
                    <Input
                        type="text"
                        id="siblingsInfo"
                        name="patientSiblingsInfo"
                        placeholder="Names & ages of siblings"
                    />
                </Label>
            </FlexGroup>

            <Label htmlFor="email">
                <span>Email address</span>
                <Input type="email" id="email" name="emailAddress" placeholder="Email Address" />
            </Label>

            <FlexGroup>
                <Label htmlFor="homePhone">
                    <span>Home phone</span>
                    <Input type="tel" id="homePhone" name="homePhone" placeholder="(123)456-7890" />
                </Label>

                <Label htmlFor="cellPhone">
                    <span>Cell phone</span>
                    <Input type="tel" id="cellPhone" name="cellPhone" placeholder="(123)456-7890" />
                </Label>

                <Label htmlFor="workPhone">
                    <span>Work phone</span>
                    <Input type="tel" id="workPhone" name="workPhone" placeholder="(123)456-7890" />
                </Label>
            </FlexGroup>

            <FlexGroup oneBig>
                <Label htmlFor="homeAddress">
                    <span>Home address</span>
                    <Input
                        type="text"
                        id="homeAddress"
                        name="homeAddress"
                        placeholder="Home address"
                    />
                </Label>

                <Label htmlFor="city">
                    <span>City</span>
                    <Input type="text" id="city" name="city" placeholder="City" />
                </Label>
                <Label htmlFor="state">
                    <span>State</span>
                    <Input type="text" id="state" name="state" placeholder="State" />
                </Label>

                <Label htmlFor="zipCode">
                    <span>Zip code</span>
                    <Input type="number" id="zipCode" name="zipCode" placeholder="Zip code" />
                </Label>
            </FlexGroup>

            <Label htmlFor="custody">
                <span>Who has legal custody of patient?</span>
                <Input
                    type="text"
                    id="custody"
                    name="legalCustody"
                    placeholder="Person(s) name who has legal custody of patient"
                />
            </Label>

            <FlexGroup oneBig>
                <Label htmlFor="fathersName">
                    <span>Father’s / Guardian’s name</span>
                    <Input
                        type="text"
                        id="fathersName"
                        name="fathersName"
                        placeholder="Father’s / Guardian’s name"
                    />
                </Label>

                <Label htmlFor="fathersSSN">
                    <span>Father’s / Guardian’s SSN</span>
                    <Input type="number" id="fathersSSN" name="fathersSSN" placeholder="SSN" />
                </Label>

                <Label htmlFor="fathersDOB">
                    <span>Father’s / Guardian’s DOB</span>
                    <Input
                        type="date"
                        id="fathersDOB"
                        name="fathersDOB"
                        placeholder="Father’s / Guardian’s DOB"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup oneBig>
                <Label htmlFor="mothersName">
                    <span>Mother’s / Guardian’s name</span>
                    <Input
                        type="text"
                        id="mothersName"
                        name="mothersName"
                        placeholder="Mother’s / Guardian’s name"
                    />
                </Label>

                <Label htmlFor="mothersSSN">
                    <span>Mother’s / Guardian’s SSN</span>
                    <Input type="number" id="mothersSSN" name="mothersSSN" placeholder="SSN" />
                </Label>

                <Label htmlFor="mothersDOB">
                    <span>Mother’s / Guardian’s DOB</span>
                    <Input
                        type="date"
                        id="mothersDOB"
                        name="mothersDOB"
                        placeholder="Mother's / Guardian's DOB"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup oneTwo>
                <Label htmlFor="referrer">
                    <span>Whom may we thank for referring you to us?</span>
                    <Input type="text" id="referrer" name="referrer" placeholder="Referrer" />
                </Label>

                <Label htmlFor="reasonForVisit">
                    <span>What is the reason for your child’s dental visit?</span>
                    <Input
                        type="text"
                        id="reasonForVisit"
                        name="reasonForVisit"
                        placeholder="Reason for visit?"
                    />
                </Label>
            </FlexGroup>

            <span className="form-divider" />

            <h3>Emergency Contact</h3>

            <FlexGroup>
                <Label htmlFor="emergencyContactName1">
                    <span>Name</span>
                    <Input
                        type="text"
                        id="emergencyContactName1"
                        name="emergencyContactName1"
                        placeholder="Emergency contact name"
                    />
                </Label>

                <Label htmlFor="emergencyContactRelationship1">
                    <span>Relationship</span>
                    <Input
                        type="text"
                        id="emergencyContactRelationship1"
                        name="emergencyContactRelationship1"
                        placeholder="Emergency contact relationship"
                    />
                </Label>

                <Label htmlFor="emergencyContactPhone1">
                    <span>Phone number</span>
                    <Input
                        type="tel"
                        id="emergencyContactPhone1"
                        name="emergencyContactPhone1"
                        placeholder="(123)456-7890"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup>
                <Label htmlFor="emergencyContactName2">
                    <span>Name</span>
                    <Input
                        type="text"
                        id="emergencyContactName2"
                        name="emergencyContactName2"
                        placeholder="Emergency contact name"
                    />
                </Label>

                <Label htmlFor="emergencyContactRelationship2">
                    <span>Relationship</span>
                    <Input
                        type="text"
                        id="emergencyContactRelationship2"
                        name="emergencyContactRelationship2"
                        placeholder="Emergency contact relationship"
                    />
                </Label>

                <Label htmlFor="emergencyContactPhone2">
                    <span>Phone number</span>
                    <Input
                        type="tel"
                        id="emergencyContactPhone2"
                        name="emergencyContactPhone2"
                        placeholder="(123)456-7890"
                    />
                </Label>
            </FlexGroup>

            <span className="form-divider" />

            <h3>Health History</h3>

            <FlexGroup radioText>
                <div>
                    <p>Is your child in good health?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childHealth-1"
                                name="childHealth"
                                value="Yes, child is in good health"
                            />
                            <Label htmlFor="childHealth-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childHealth-2"
                                name="childHealth"
                                value="No, child is not in good health"
                            />
                            <Label htmlFor="childHealth-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
                <Label htmlFor="childPhysicalExamDate">
                    <span>Date of last physical exam</span>
                    <Input
                        type="date"
                        id="childPhysicalExamDate"
                        name="childPhysicalExamDate"
                        placeholder="Date of last physical exam"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup twoOne>
                <Label htmlFor="childPhysiciansName">
                    <span>Name of child’s physician</span>
                    <Input
                        type="text"
                        id="childPhysiciansName"
                        name="childPhysiciansName"
                        placeholder="Name of child’s physician"
                    />
                </Label>

                <Label htmlFor="childPhysiciansNumber">
                    <span>Child’s physician’s phone number</span>
                    <Input
                        type="tel"
                        id="childPhysiciansNumber"
                        name="childPhysiciansNumber"
                        placeholder="Child’s physician’s number"
                    />
                </Label>
            </FlexGroup>

            <div>
                <p>Has your child ever had a health problem?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childHealthProblems-1"
                                    name="childHealthProblems"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childHealthProblems-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childHealthProblems-2"
                                    name="childHealthProblems"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childHealthProblems-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childHealthProblems-3" commonLabel hiddenLabel>
                                <span>
                                    If your child ever had a health problem, please describe here
                                </span>
                                <Input
                                    type="text"
                                    id="childHealthProblems-3"
                                    name={value === 'Yes' ? 'childHealthProblems' : ''}
                                    placeholder="If yes, please describe"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <div>
                <p>Has your child ever been hospitalized or had surgery?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childHospitalization-1"
                                    name="childHospitalization"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childHospitalization-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childHospitalization-2"
                                    name="childHospitalization"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childHospitalization-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childHospitalization-3" commonLabel hiddenLabel>
                                <span>
                                    If your child ever has been hospitalized, please describe here
                                </span>
                                <Input
                                    type="text"
                                    id="childHospitalization-3"
                                    name={value === 'Yes' ? 'childHospitalization' : ''}
                                    placeholder="If yes, please give reason and dates"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <div>
                <p>Was there excessive bleeding when cut?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childExcessiveBleeding-1"
                                    name="childExcessiveBleeding"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childExcessiveBleeding-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childExcessiveBleeding-2"
                                    name="childExcessiveBleeding"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childExcessiveBleeding-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childExcessiveBleeding-3" commonLabel hiddenLabel>
                                <span>
                                    If there was excessive bleeding when cut, please describe here
                                </span>
                                <Input
                                    type="text"
                                    id="childExcessiveBleeding-3"
                                    name={value === 'Yes' ? 'childExcessiveBleeding' : ''}
                                    placeholder="If yes, please describe"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <div>
                <p>Is your child allergic to anything?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childAllergy-1"
                                    name="childAllergy"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childAllergy-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childAllergy-2"
                                    name="childAllergy"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childAllergy-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childAllergy-3" commonLabel hiddenLabel>
                                <span>If child is allergic to, please describe here</span>
                                <Input
                                    type="text"
                                    id="childAllergy-3"
                                    name={value === 'Yes' ? 'childAllergy' : ''}
                                    placeholder="If yes, please describe"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <div>
                <p>Is your child currently taking any medications?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childTakingMedications-1"
                                    name="childTakingMedications"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childTakingMedications-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childTakingMedications-2"
                                    name="childTakingMedications"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childTakingMedications-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childTakingMedications-3" commonLabel hiddenLabel>
                                <span>
                                    If child is taking any medications, please give medication and
                                    reason
                                </span>
                                <Input
                                    type="text"
                                    id="childTakingMedications-3"
                                    name={value === 'Yes' ? 'childTakingMedications' : ''}
                                    placeholder="If yes, please give medication and reason"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <div>
                <p>Were there any problems at birth?</p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childProblemAtBirth-1"
                                    name="childProblemAtBirth"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="childProblemAtBirth-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="childProblemAtBirth-2"
                                    name="childProblemAtBirth"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="childProblemAtBirth-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="childProblemAtBirth-3" commonLabel hiddenLabel>
                                <span>If there was any problem at birth, please describe</span>
                                <Input
                                    type="text"
                                    id="childProblemAtBirth-3"
                                    name={value === 'Yes' ? 'childProblemAtBirth' : ''}
                                    placeholder="If yes, please describe"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <CheckBoxes>
                <p>Please check if your child has been treated for any of the following:</p>
                <div>
                    {childTreatments.map((treatment, i) => (
                        <InputGroup key={i}>
                            <Input
                                type="checkbox"
                                id={`childTreatments-${i}`}
                                name="childTreatments"
                                value={treatment}
                            />
                            <Label htmlFor={`childTreatments-${i}`}>{treatment}</Label>
                        </InputGroup>
                    ))}
                </div>
                <Label htmlFor="childTreatmentsExplanation" hiddenLabel>
                    <span>Please explain any treatments you have checked above</span>
                    <Input
                        type="text"
                        id="childTreatmentsExplanation"
                        name="childTreatmentsExplanation"
                        placeholder="Please explain any treatments you have checked above"
                    />
                </Label>
            </CheckBoxes>

            <span className="form-divider" />

            <h3>Dental History</h3>

            <FlexGroup radioText>
                <div>
                    <p>Has your child ever had a dental visit?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="previousVisit-1"
                                name="previousVisit"
                                value="Yes"
                            />
                            <Label htmlFor="previousVisit-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="previousVisit-2"
                                name="previousVisit"
                                value="No"
                            />
                            <Label htmlFor="previousVisit-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
                <Label htmlFor="previousDentistName">
                    <span>Name of dentist</span>
                    <Input
                        type="text"
                        id="previousDentistName"
                        name="previousDentistName"
                        placeholder="Name of dentist"
                    />
                </Label>
                <Label htmlFor="previousVisitDate">
                    <span>Previous visit date</span>
                    <Input
                        type="date"
                        id="previousVisitDate"
                        name="previousVisitDate"
                        placeholder="Previous visit date"
                    />
                </Label>
            </FlexGroup>

            <div>
                <p>
                    Has your child experienced any unfavorable reaction from previous dental care?
                </p>
                <FormRadioState>
                    {(value, handleChange) => (
                        <RadioGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="previousVisitReaction-1"
                                    name="previousVisitReaction"
                                    value="Yes"
                                    onChange={handleChange}
                                    checked={value === 'Yes'}
                                />
                                <Label htmlFor="previousVisitReaction-1">Yes</Label>
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="radio"
                                    id="previousVisitReaction-2"
                                    name="previousVisitReaction"
                                    value="No"
                                    onChange={handleChange}
                                    checked={value === 'No'}
                                />
                                <Label htmlFor="previousVisitReaction-2">No</Label>
                            </InputGroup>
                            <Label htmlFor="previousVisitReaction-3" commonLabel hiddenLabel>
                                <span>
                                    If there were any unfavorable reactions from previous dental
                                    visit, please explain
                                </span>
                                <Input
                                    type="text"
                                    id="previousVisitReaction-3"
                                    name={value === 'Yes' ? 'previousVisitReaction' : ''}
                                    placeholder="If yes, please explain"
                                    disabled={value !== 'Yes'}
                                    required={value === 'Yes'}
                                />
                            </Label>
                        </RadioGroup>
                    )}
                </FormRadioState>
            </div>

            <FlexGroup radioText>
                <div>
                    <p>Does your child suck a finger, thumb, or pacifier?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childSucksFinger-1"
                                name="childSucksFinger"
                                value="Yes child do suck a finger, thumb or pacifier."
                            />
                            <Label htmlFor="childSucksFinger-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childSucksFinger-2"
                                name="childSucksFinger"
                                value="No child does not suck a finger, thumb or pacifier."
                            />
                            <Label htmlFor="childSucksFinger-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            <FlexGroup radioText>
                <div>
                    <p>Does your child have pain with chewing, yawning, or wide opening?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childPainChewing-1"
                                name="childPainChewing"
                                value="Yes child has pain with chewing, yawning, or wide opening."
                            />
                            <Label htmlFor="childPainChewing-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childPainChewing-2"
                                name="childPainChewing"
                                value="No child does not has pain with chewing, yawning, or wide opening."
                            />
                            <Label htmlFor="childPainChewing-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            <FlexGroup radioText>
                <div>
                    <p>Does your child breathe mainly through the mouth?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childBreatheMouth-1"
                                name="childBreatheMouth"
                                value="Yes child breathe mainly through the mouth."
                            />
                            <Label htmlFor="childBreatheMouth-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childBreatheMouth-2"
                                name="childBreatheMouth"
                                value="No child does not breathe mainly through the mouth."
                            />
                            <Label htmlFor="childBreatheMouth-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            <FlexGroup radioText>
                <div>
                    <p>Does your child grind his/her teeth?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childGrindTeeth-1"
                                name="childGrindTeeth"
                                value="Yes child does grind his/her teeth."
                            />
                            <Label htmlFor="childGrindTeeth-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childGrindTeeth-2"
                                name="childGrindTeeth"
                                value="No child does not grind his/her teeth."
                            />
                            <Label htmlFor="childGrindTeeth-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            <FlexGroup radioText>
                <div>
                    <p>Does your child snore?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childSnoring-1"
                                name="childSnoring"
                                value="Yes child does snore."
                            />
                            <Label htmlFor="childSnoring-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="childSnoring-2"
                                name="childSnoring"
                                value="No child does not snore."
                            />
                            <Label htmlFor="childSnoring-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            <CheckBoxes>
                <p>Please check if your child is having problems with any of the following:</p>
                <div>
                    {childProblems.map((problems, i) => (
                        <InputGroup key={i}>
                            <Input
                                type="checkbox"
                                id={`childProblems-${i}`}
                                name="childProblems"
                                value={problems}
                            />
                            <Label htmlFor={`childProblems-${i}`}>{problems}</Label>
                        </InputGroup>
                    ))}
                </div>
                <Label htmlFor="childProblemsComment" hiddenLabel>
                    <span>Please comment about health problems you checked above</span>
                    <Input
                        type="text"
                        id="childProblemsComment"
                        name="childProblemsComment"
                        placeholder="Comments"
                    />
                </Label>
            </CheckBoxes>

            <span className="form-divider" />

            <h3>Insurance Company</h3>

            <FlexGroup twoOne>
                <Label htmlFor="insuranceCompany">
                    <span>Insurance company</span>
                    <Input
                        type="text"
                        id="insuranceCompany"
                        name="insuranceCompany"
                        placeholder="Insurance company"
                    />
                </Label>
                <Label htmlFor="insuranceGroup">
                    <span>Group #</span>
                    <Input
                        type="text"
                        id="insuranceGroup"
                        name="insuranceGroup"
                        placeholder="Group"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup twoOne>
                <Label htmlFor="insuranceSubscriberName">
                    <span>Subscriber’s name</span>
                    <Input
                        type="text"
                        id="insuranceSubscriberName"
                        name="insuranceSubscriberName"
                        placeholder="Subscriber’s name"
                    />
                </Label>
                <Label htmlFor="insuranceSubscriberDOB">
                    <span>Subscriber’s DOB</span>
                    <Input
                        type="date"
                        id="insuranceSubscriberDOB"
                        name="insuranceSubscriberDOB"
                        placeholder="subscriber DOB"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup oneTwo>
                <Label htmlFor="insuranceSubscriberSSN">
                    <span>Subscriber’s SSN</span>
                    <Input
                        type="number"
                        id="insuranceSubscriberSSN"
                        name="insuranceSubscriberSSN"
                        placeholder="Subscriber’s SSN"
                    />
                </Label>
                <Label htmlFor="insuranceRelationshipToPatient">
                    <span>Relationship to Patient</span>
                    <Input
                        type="text"
                        id="insuranceRelationshipToPatient"
                        name="insuranceRelationshipToPatient"
                        placeholder="Relationship to Patient"
                    />
                </Label>
            </FlexGroup>

            <FlexGroup radioText>
                <div>
                    <p>Is patient covered by additional insurance?</p>
                    <RadioGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="additionalInsurance-1"
                                name="additionalInsurance"
                                value="Yes patient is covered by additional insurance."
                                onChange={() => setAdditionalInsurance(true)}
                            />
                            <Label htmlFor="additionalInsurance-1">Yes</Label>
                        </InputGroup>
                        <InputGroup>
                            <Input
                                type="radio"
                                id="additionalInsurance-2"
                                name="additionalInsurance"
                                value="No patient is not covered by additional insurance."
                                onChange={() => setAdditionalInsurance(false)}
                            />
                            <Label htmlFor="additionalInsurance-2">No</Label>
                        </InputGroup>
                    </RadioGroup>
                </div>
            </FlexGroup>

            {additionalInsurance ? (
                <Fragment>
                    <FlexGroup twoOne>
                        <Label htmlFor="additionalInsuranceCompany">
                            <span>Insurance company</span>
                            <Input
                                type="text"
                                id="additionalInsuranceCompany"
                                name="additionalInsuranceCompany"
                                placeholder="Insurance company"
                            />
                        </Label>
                        <Label htmlFor="additionalInsuranceGroup">
                            <span>Group #</span>
                            <Input
                                type="text"
                                id="additionalInsuranceGroup"
                                name="additionalInsuranceGroup"
                                placeholder="Group"
                            />
                        </Label>
                    </FlexGroup>

                    <FlexGroup twoOne>
                        <Label htmlFor="additionalInsuranceSubscriberName">
                            <span>Subscriber’s name</span>
                            <Input
                                type="text"
                                id="additionalInsuranceSubscriberName"
                                name="additionalInsuranceSubscriberName"
                                placeholder="Subscriber’s name"
                            />
                        </Label>
                        <Label htmlFor="additionalInsuranceSubscriberDOB">
                            <span>Subscriber’s DOB</span>
                            <Input
                                type="date"
                                id="additionalInsuranceSubscriberDOB"
                                name="additionalInsuranceSubscriberDOB"
                                placeholder="subscriber DOB"
                            />
                        </Label>
                    </FlexGroup>

                    <FlexGroup oneTwo>
                        <Label htmlFor="additionalInsuranceSubscriberSSN">
                            <span>Subscriber’s SSN</span>
                            <Input
                                type="number"
                                id="additionalInsuranceSubscriberSSN"
                                name="additionalInsuranceSubscriberSSN"
                                placeholder="Subscriber’s SSN"
                            />
                        </Label>
                        <Label htmlFor="additionalInsuranceRelationshipToPatient">
                            <span>Relationship to Patient</span>
                            <Input
                                type="text"
                                id="additionalInsuranceRelationshipToPatient"
                                name="additionalInsuranceRelationshipToPatient"
                                placeholder="Relationship to Patient"
                            />
                        </Label>
                    </FlexGroup>
                </Fragment>
            ) : (
                ''
            )}

            <p>
                ASSIGNMENT AND RELEASE
                <br />
                <br />
                I, the undersigned certify that I (or my dependent) have insurance cover with the
                insurance company(ies) above and assign payment directly to doctor. I understand
                that I am financially responsible for all changes whether or not paid by insurance.
                I hereby authorize the doctor to release all information necessary to secure the
                payment of benefits. I authorize the use of this signature on all insurance
                submissions.
            </p>

            <FlexGroup oneBig>
                <Label htmlFor="responsiblePartySignature">
                    <span>Responsible party signature</span>
                    <Input
                        type="text"
                        id="responsiblePartySignature"
                        name="responsiblePartySignature"
                        placeholder="Responsible party signature"
                    />
                </Label>
                <Label htmlFor="responsiblePartyRelationshipToPatient">
                    <span>Relationship</span>
                    <Input
                        type="text"
                        id="responsiblePartyRelationshipToPatient"
                        name="responsiblePartyRelationshipToPatient"
                        placeholder="Your name here"
                    />
                </Label>
                <Label htmlFor="responsibleSignatureDate">
                    <span>Date</span>
                    <Input
                        type="date"
                        id="responsibleSignatureDate"
                        name="responsibleSignatureDate"
                        placeholder="date"
                    />
                </Label>
            </FlexGroup>

            <span className="form-divider" />

            <h3>Consent for Dental Treatment</h3>

            <p>
                I request and authorize any doctor at Kidz Dental Care to examine, clean, and
                provide dental treatment on my child’s teeth. I further request and authorize the
                taking of dental x-rays as may be considered necessary by any doctor at Kidz Dental
                Care to diagnose and/or treat my child’s dental problem. I understand that dental
                treatment for children includes efforts to guide their behavior by helping them to
                understand the treatment in terms appropriate for their age. Any doctor at Kidz
                Dental Care will provide an environment likely to help children learn to cooperate
                during treatment by using praise, explanation, and demonstration of procedures and
                instruments, using variable voice tone and stabilization with a pedowrap. I will be
                responsible for any charges incurred on this child for dental treatment. <br />{' '}
                <br />I understand that collection actions may taken if my balance goes beyond 90
                days. <br /> <br />I consent to the use and disclosure of my protected health
                information to obtain payment information in connection with my dental claims.
            </p>

            <FlexGroup twoOne>
                <Label htmlFor="parentGuardianSignature">
                    <span>Parent/Guardian signature</span>
                    <Input
                        type="text"
                        id="parentGuardianSignature"
                        name="parentGuardianSignature"
                        placeholder="Parent/Guardian signature"
                    />
                </Label>
                <Label htmlFor="parentGuardianSignatureDate">
                    <span>Date</span>
                    <Input
                        type="date"
                        id="parentGuardianSignatureDate"
                        name="parentGuardianSignatureDate"
                        placeholder="date"
                    />
                </Label>
            </FlexGroup>

            <ButtonPrimary type="submit" disabled={submit === 'submitting'} color="blue" fill>
                {submit === 'submitting' ? 'Sending...' : 'Submit Form'}
            </ButtonPrimary>
            <p
                style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    display: `${submit === 'submitted' ? 'block' : 'none'}`,
                }}
            >
                Thank You! Your message has been sent.
            </p>
            <p
                style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    display: `${submit === 'error' ? 'block' : 'none'}`,
                }}
            >
                Something went wrong, please call us instead.
            </p>
        </Form>
    );
};
